import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiChevronUp,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiCheckCircle,
  FiX,
  FiFileText,
  FiRepeat,
  FiSlash,
  FiPlus
} from 'react-icons/fi';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import { useContextoSesion } from '../../miscelaneo/ContextoSesion';
import SpinnerCargando from '../util/SpinnerCargando';
import PlantillaModal from '../util/PlantillaModal';
import FuncionesGenerales from '../util/FuncionesGenerales';
import ModalDetalleCotizacion from './ModalDetalleCotizacion';

// =====================================================
// COMPONENTE: Tarjeta de Resumen (mismo patrón que PantallaCxC)
// =====================================================
function TarjetaResumen({ titulo, valor, subtitulo, colorBorde, colorTexto, activo, onClick }) {
  return (
    <div
      className={`bg-white rounded-xl border shadow-md p-4 border-l-4 ${colorBorde} transition-all cursor-pointer hover:shadow-lg ${
        activo ? 'ring-2 ring-blue-400 ring-offset-1' : 'border-gray-300'
      }`}
      onClick={onClick}
    >
      <p className="text-sm text-gray-500">{titulo}</p>
      <p className={`text-2xl font-bold mt-1 ${colorTexto}`}>{valor}</p>
      {subtitulo && <p className="text-xs text-gray-400 mt-1">{subtitulo}</p>}
    </div>
  );
}

// =====================================================
// Helper: badge de estado de cotización
// =====================================================
function BadgeEstado({ id_estado, id_factura_convertida }) {
  if (id_factura_convertida) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <FiRepeat size={12} />
        Convertida
      </span>
    );
  }
  if (id_estado === 2) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <FiSlash size={12} />
        Anulada
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
      <FiCheckCircle size={12} />
      Activa
    </span>
  );
}

// =====================================================
// COMPONENTE PRINCIPAL: Pantalla Cotizaciones
// =====================================================
export default function PantallaCotizaciones() {
  const navigate = useNavigate();
  const { usuario, cargando: cargandoContexto, tienePermiso } = useContextoSesion();
  const { formatoMoneda } = FuncionesGenerales(usuario);

  // Estado de datos
  const [cotizaciones, setCotizaciones] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [cargando, setCargando] = useState(true);

  // Estado de filtros (API-driven)
  const [filtros, setFiltros] = useState({
    busqueda: '',
    id_estado: '',       // '' = todas, '1' = activa, '2' = anulada, 'convertida'
    fecha_desde: '',
    fecha_hasta: '',
    ordenar_por: 'fecha_hora_creacion',
    orden: 'DESC'
  });

  // Estado de filtros temporales (UI-driven, aplican al Buscar)
  const [filtrosTemporales, setFiltrosTemporales] = useState({
    busqueda: '',
    fecha_desde: '',
    fecha_hasta: '',
    ordenar_por: 'fecha_hora_creacion',
    orden: 'DESC'
  });

  // Estado de paginación
  const [paginacion, setPaginacion] = useState({
    limite: 25,
    offset: 0,
    total: 0
  });

  // Estado de UI
  const [filtrosExpandidos, setFiltrosExpandidos] = useState(false);

  // Modal detalle
  const [idCotizacionDetalle, setIdCotizacionDetalle] = useState(null);
  const [mostrarDetalle, setMostrarDetalle] = useState(false);

  // Modal convertir
  const [cotizacionAConvertir, setCotizacionAConvertir] = useState(null);
  const [convertirDestino, setConvertirDestino] = useState('factura_demo');
  const [convirtiendo, setConvirtiendo] = useState(false);

  // Cargar datos
  const cargarDatos = useCallback(async () => {
    try {
      setCargando(true);

      const params = {
        limite: paginacion.limite,
        offset: paginacion.offset,
        ordenar_por: filtros.ordenar_por,
        orden: filtros.orden
      };
      if (filtros.busqueda)    params.busqueda    = filtros.busqueda;
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
      if (filtros.id_estado === 'convertida') {
        params.convertida = 'true';
      } else if (filtros.id_estado) {
        params.id_estado = filtros.id_estado;
      }

      const response = await instanciaAxios.get('/cotizacion', { params });
      const data = response.data;
      setCotizaciones(data.cotizaciones || []);
      setResumen(data.resumen || { total: 0, activas: 0, anuladas: 0, convertidas: 0 });
      setPaginacion(prev => ({ ...prev, total: data.total || 0 }));
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al cargar cotizaciones:', error);
      toast.error('Error al cargar cotizaciones');
    } finally {
      setCargando(false);
    }
  }, [filtros, paginacion.limite, paginacion.offset]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Helpers
  const formatearFecha = (fecha) => {
    if (!fecha) return '—';
    return new Date(fecha).toLocaleDateString('es-DO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Handlers
  const handleOrdenar = (columna) => {
    setFiltros(prev => ({
      ...prev,
      ordenar_por: columna,
      orden: prev.ordenar_por === columna && prev.orden === 'ASC' ? 'DESC' : 'ASC'
    }));
    setPaginacion(prev => ({ ...prev, offset: 0 }));
  };

  const handleBuscar = () => {
    setFiltros(prev => ({
      ...prev,
      busqueda: filtrosTemporales.busqueda.trim(),
      fecha_desde: filtrosTemporales.fecha_desde,
      fecha_hasta: filtrosTemporales.fecha_hasta,
      ordenar_por: filtrosTemporales.ordenar_por
    }));
    setPaginacion(prev => ({ ...prev, offset: 0 }));
  };

  const handleLimpiarFiltros = () => {
    setFiltros({
      busqueda: '',
      id_estado: '',
      fecha_desde: '',
      fecha_hasta: '',
      ordenar_por: 'fecha_hora_creacion',
      orden: 'DESC'
    });
    setFiltrosTemporales({
      busqueda: '',
      fecha_desde: '',
      fecha_hasta: '',
      ordenar_por: 'fecha_hora_creacion',
      orden: 'DESC'
    });
    setPaginacion(prev => ({ ...prev, offset: 0 }));
  };

  const handleEstadoClick = (estado) => {
    const nuevoEstado = filtros.id_estado === estado ? '' : estado;
    setFiltros(prev => ({ ...prev, id_estado: nuevoEstado }));
    setPaginacion(prev => ({ ...prev, offset: 0 }));
  };

  // Ver detalle
  const verDetalle = (cotizacion) => {
    setIdCotizacionDetalle(cotizacion.id_cotizacion);
    setMostrarDetalle(true);
  };

  // Convertir
  const confirmarConversion = async () => {
    if (!cotizacionAConvertir) return;
    try {
      setConvirtiendo(true);
      const { data } = await instanciaAxios.get(`/cotizacion/${cotizacionAConvertir.id_cotizacion}`);
      setCotizacionAConvertir(null);
      const ruta = convertirDestino === 'pos' ? '/ProcesoPOS' : '/FacturaDemo';
      navigate(ruta, { state: { cotizacionOrigen: data } });
    } catch (error) {
      if (esErrorPermiso(error)) return;
      toast.error('Error al cargar la cotización para conversión');
    } finally {
      setConvirtiendo(false);
    }
  };

  // Indicador de ordenamiento
  const OrdenIndicador = ({ columna }) => {
    if (filtros.ordenar_por !== columna) return null;
    return filtros.orden === 'ASC'
      ? <FiChevronUp className="inline ml-1" size={14} />
      : <FiChevronDown className="inline ml-1" size={14} />;
  };

  // Verificar si hay filtros activos
  const hayFiltrosActivos = useMemo(() => {
    return filtros.busqueda || filtros.id_estado ||
           filtros.fecha_desde || filtros.fecha_hasta;
  }, [filtros]);

  // Condición de fila editable/accionable
  const esAccionable = (cot) => cot.id_estado === 1 && !cot.id_factura_convertida;

  if (cargandoContexto || (cargando && !resumen)) {
    return <SpinnerCargando />;
  }

  return (
    <div className="bg-gray-200 min-h-screen">
      <div className="flex justify-center py-10">
        <div className="bg-gray-100 bg-opacity-90 rounded-xl border border-gray-100 p-12 w-full max-w-7xl mx-10">
          <div className="flex flex-col gap-8">

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="font-cascadia font-bold text-2xl">Cotizaciones</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={cargarDatos}
            disabled={cargando}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            title="Actualizar"
          >
            <FiRefreshCw size={20} className={cargando ? 'animate-spin' : ''} />
          </button>
          {tienePermiso('cotizacion_crear') && (
            <button
              onClick={() => navigate('/ProcesoCotizacion')}
              className="flex items-center gap-2 px-4 py-2 bg-color-primario text-white rounded-xl hover:bg-opacity-90 transition-colors font-medium"
            >
              <FiPlus size={16} />
              Nueva Cotización
            </button>
          )}
        </div>
      </div>

      {/* Resumen */}
      {resumen && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <TarjetaResumen
            titulo="Total Cotizaciones"
            valor={resumen.total}
            subtitulo="todas las cotizaciones"
            colorBorde="border-l-color-primario"
            colorTexto="text-gray-800"
            activo={filtros.id_estado === ''}
            onClick={() => handleEstadoClick('')}
          />
          <TarjetaResumen
            titulo="Activas"
            valor={resumen.activas}
            subtitulo="pendientes de conversión"
            colorBorde="border-l-blue-500"
            colorTexto="text-blue-600"
            activo={filtros.id_estado === '1'}
            onClick={() => handleEstadoClick('1')}
          />
          <TarjetaResumen
            titulo="Convertidas"
            valor={resumen.convertidas}
            subtitulo="ya facturadas"
            colorBorde="border-l-green-500"
            colorTexto="text-green-600"
            activo={filtros.id_estado === 'convertida'}
            onClick={() => handleEstadoClick('convertida')}
          />
          <TarjetaResumen
            titulo="Anuladas"
            valor={resumen.anuladas}
            subtitulo="sin efecto"
            colorBorde="border-l-red-500"
            colorTexto="text-red-600"
            activo={filtros.id_estado === '2'}
            onClick={() => handleEstadoClick('2')}
          />
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-300 shadow-md overflow-hidden">
        <div className="p-4 flex flex-wrap items-center gap-4">
          <select
            className="px-4 py-2 border border-gray-400 rounded-xl focus:ring-2 focus:ring-color-primario focus:border-color-primario transition-colors min-w-[180px]"
            value={filtros.id_estado}
            onChange={(e) => {
              setFiltros(prev => ({ ...prev, id_estado: e.target.value }));
              setPaginacion(prev => ({ ...prev, offset: 0 }));
            }}
          >
            <option value="">Todas las cotizaciones</option>
            <option value="1">Activas</option>
            <option value="convertida">Convertidas</option>
            <option value="2">Anuladas</option>
          </select>

          <div className="relative min-w-[220px] flex-1 max-w-xs">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              value={filtrosTemporales.busqueda}
              onChange={(e) => setFiltrosTemporales(prev => ({ ...prev, busqueda: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') handleBuscar(); }}
              placeholder="Buscar por # o cliente..."
              className="w-full pl-9 pr-3 py-2 border border-gray-400 rounded-xl focus:ring-2 focus:ring-color-primario focus:border-color-primario transition-colors"
            />
          </div>

          <button
            onClick={() => setFiltrosExpandidos(!filtrosExpandidos)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-xl transition-colors ${
              filtrosExpandidos || hayFiltrosActivos
                ? 'border-color-primario text-color-primario bg-blue-50'
                : 'border-gray-400 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <FiFilter size={18} />
            Filtros
            {hayFiltrosActivos && (
              <span className="w-2 h-2 bg-color-primario rounded-full" />
            )}
          </button>

          <button
            onClick={handleBuscar}
            className="flex items-center gap-2 px-4 py-2 bg-color-primario text-white rounded-xl hover:bg-green-700 transition-colors"
          >
            <FiSearch size={16} />
            Buscar
          </button>

          {hayFiltrosActivos && (
            <button
              onClick={handleLimpiarFiltros}
              className="flex items-center gap-1 px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <FiX size={16} />
              Limpiar
            </button>
          )}
        </div>

        {/* Filtros expandidos */}
        {filtrosExpandidos && (
          <div className="px-4 pb-4 pt-2 border-t border-gray-200 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Fecha Desde</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-400 rounded-xl focus:ring-2 focus:ring-color-primario focus:border-color-primario"
                value={filtrosTemporales.fecha_desde}
                onChange={(e) => {
                  setFiltrosTemporales(prev => ({ ...prev, fecha_desde: e.target.value }));
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Fecha Hasta</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-400 rounded-xl focus:ring-2 focus:ring-color-primario focus:border-color-primario"
                value={filtrosTemporales.fecha_hasta}
                onChange={(e) => {
                  setFiltrosTemporales(prev => ({ ...prev, fecha_hasta: e.target.value }));
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Ordenar Por</label>
              <select
                className="w-full px-3 py-2 border border-gray-400 rounded-xl focus:ring-2 focus:ring-color-primario focus:border-color-primario"
                value={filtrosTemporales.ordenar_por}
                onChange={(e) => {
                  setFiltrosTemporales(prev => ({ ...prev, ordenar_por: e.target.value }));
                }}
              >
                <option value="fecha_hora_creacion">Fecha Creación</option>
                <option value="id_cotizacion">Número</option>
                <option value="monto_total">Monto Total</option>
                <option value="nombre_cliente">Cliente</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl border border-gray-300 shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-color-primario text-white">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                  onClick={() => handleOrdenar('id_cotizacion')}
                >
                  # <OrdenIndicador columna="id_cotizacion" />
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                  onClick={() => handleOrdenar('nombre_cliente')}
                >
                  Cliente <OrdenIndicador columna="nombre_cliente" />
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                  onClick={() => handleOrdenar('fecha_hora_creacion')}
                >
                  Fecha <OrdenIndicador columna="fecha_hora_creacion" />
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">
                  Subtotal
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">
                  ITBIS
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                  onClick={() => handleOrdenar('monto_total')}
                >
                  Total <OrdenIndicador columna="monto_total" />
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">
                  Factura
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {cotizaciones.map((cot) => (
                <tr
                  key={cot.id_cotizacion}
                  className="hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => verDetalle(cot)}
                >
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm font-semibold text-color-primario">#{cot.id_cotizacion}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900 truncate max-w-[200px]">
                        {cot.nombre_cliente || 'Consumidor Final'}
                      </p>
                      {cot.rnc_cliente && (
                        <p className="text-xs text-gray-500">{cot.rnc_cliente}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatearFecha(cot.fecha_hora_creacion)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-600">
                    {formatoMoneda(parseFloat(cot.monto) || 0)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-600">
                    {formatoMoneda(parseFloat(cot.impuesto_total) || 0)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-semibold text-gray-800">
                      {formatoMoneda(parseFloat(cot.monto_total) || 0)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <BadgeEstado id_estado={cot.id_estado} id_factura_convertida={cot.id_factura_convertida} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    {cot.id_factura_convertida ? (
                      <span className="font-mono text-sm text-green-700 font-semibold">
                        #{cot.id_factura_convertida}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          verDetalle(cot);
                        }}
                        className="px-3 py-1.5 bg-color-primario text-white text-sm font-medium rounded-xl hover:bg-opacity-90 transition-colors inline-flex items-center gap-1"
                      >
                        <FiFileText size={14} />
                        Ver
                      </button>
                      {esAccionable(cot) && tienePermiso('cotizacion_convertir') && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setCotizacionAConvertir(cot); setConvertirDestino('factura_demo'); }}
                          title="Convertir a factura"
                          className="p-2 rounded-xl text-gray-500 hover:text-green-600 hover:bg-green-50 transition-colors"
                        >
                          <FiRepeat size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}

              {cotizaciones.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <FiFileText className="mx-auto text-gray-300 mb-3" size={48} />
                    <p className="text-gray-500">No se encontraron cotizaciones</p>
                    {hayFiltrosActivos && (
                      <button
                        onClick={handleLimpiarFiltros}
                        className="mt-3 text-color-primario hover:underline font-medium"
                      >
                        Limpiar filtros
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {paginacion.total > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <p className="text-sm text-gray-600">
                Mostrando {paginacion.offset + 1} - {Math.min(paginacion.offset + paginacion.limite, paginacion.total)} de {paginacion.total}
              </p>
              <select
                className="px-2 py-1 border border-gray-300 rounded text-sm"
                value={paginacion.limite}
                onChange={(e) => setPaginacion(prev => ({ ...prev, limite: parseInt(e.target.value), offset: 0 }))}
              >
                <option value={10}>10 por página</option>
                <option value={25}>25 por página</option>
                <option value={50}>50 por página</option>
                <option value={100}>100 por página</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setPaginacion(prev => ({ ...prev, offset: 0 }))}
                disabled={paginacion.offset === 0}
                className="p-2 border border-gray-300 rounded-xl disabled:opacity-50 hover:bg-gray-100 transition-colors"
                title="Primera página"
              >
                <FiChevronsLeft size={16} />
              </button>
              <button
                onClick={() => setPaginacion(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limite) }))}
                disabled={paginacion.offset === 0}
                className="px-3 py-2 border border-gray-300 rounded-xl disabled:opacity-50 hover:bg-gray-100 transition-colors flex items-center gap-1"
              >
                <FiChevronLeft size={16} />
                Anterior
              </button>
              <button
                onClick={() => setPaginacion(prev => ({ ...prev, offset: prev.offset + prev.limite }))}
                disabled={paginacion.offset + paginacion.limite >= paginacion.total}
                className="px-3 py-2 border border-gray-300 rounded-xl disabled:opacity-50 hover:bg-gray-100 transition-colors flex items-center gap-1"
              >
                Siguiente
                <FiChevronRight size={16} />
              </button>
              <button
                onClick={() => setPaginacion(prev => ({
                  ...prev,
                  offset: Math.floor((prev.total - 1) / prev.limite) * prev.limite
                }))}
                disabled={paginacion.offset + paginacion.limite >= paginacion.total}
                className="p-2 border border-gray-300 rounded-xl disabled:opacity-50 hover:bg-gray-100 transition-colors"
                title="Última página"
              >
                <FiChevronsRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

          </div>
        </div>
      </div>

      {/* ===== MODALES ===== */}

      {/* Modal detalle de cotización */}
      <ModalDetalleCotizacion
        abierto={mostrarDetalle}
        onCerrar={() => { setMostrarDetalle(false); setIdCotizacionDetalle(null); }}
        idCotizacion={idCotizacionDetalle}
      />

      {/* Modal convertir a factura */}
      <PlantillaModal
        mostrar={!!cotizacionAConvertir}
        onClose={() => !convirtiendo && setCotizacionAConvertir(null)}
        titulo="Convertir a Factura"
      >
        {cotizacionAConvertir && (
          <div className="space-y-5">
            <p className="text-gray-600 text-sm">
              La cotización <strong>#{cotizacionAConvertir.id_cotizacion}</strong> se convertirá en una factura.
              <br />Selecciona hacia dónde deseas ir para procesar el cobro:
            </p>

            <div className="space-y-3">
              <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition ${convertirDestino === 'factura_demo' ? 'border-color-primario bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <input type="radio" name="destino" value="factura_demo"
                  checked={convertirDestino === 'factura_demo'}
                  onChange={() => setConvertirDestino('factura_demo')} />
                <div>
                  <p className="font-semibold text-sm">Factura Demo</p>
                  <p className="text-xs text-gray-500">Para facturas sin comprobante fiscal o con e-NCF</p>
                </div>
              </label>
              <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition ${convertirDestino === 'pos' ? 'border-color-primario bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <input type="radio" name="destino" value="pos"
                  checked={convertirDestino === 'pos'}
                  onChange={() => setConvertirDestino('pos')} />
                <div>
                  <p className="font-semibold text-sm">Punto de Venta (POS)</p>
                  <p className="text-xs text-gray-500">Para cobrar en caja con sesión activa</p>
                </div>
              </label>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setCotizacionAConvertir(null)}
                disabled={convirtiendo}
                className="flex-1 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarConversion}
                disabled={convirtiendo}
                className="flex-1 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <FiRepeat />
                {convirtiendo ? 'Convirtiendo...' : 'Convertir →'}
              </button>
            </div>
          </div>
        )}
      </PlantillaModal>
    </div>
  );
}
